import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FileText, ExternalLink, Loader2, Upload, ShieldCheck, Trash2 } from 'lucide-react'
import {
  fetchCollaborationDocuments,
  uploadCollaborationContract,
  deleteCollaborationDocument,
  type CollaborationDocument,
} from '@/redux/documentsSlice'
import { collaborationsApi } from '@/redux/collaborationsApi'
import type { RootState, AppDispatch } from '@/store'
import athleteApi from '@/lib/api'

interface CollaborationDocumentsCardProps {
  collaborationId: string
}

const EMPTY_DOCS: CollaborationDocument[] = []
const EMPTY_DELETING: Record<string, boolean> = {}

const CollaborationDocumentsCard = ({ collaborationId }: CollaborationDocumentsCardProps) => {
  const dispatch = useDispatch<AppDispatch>()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [openingDocId, setOpeningDocId] = useState<string | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const documents = useSelector(
    (state: RootState) => state.documents.collaborationDocuments[collaborationId] ?? EMPTY_DOCS,
  )
  const loading = useSelector(
    (state: RootState) => state.documents.collaborationDocumentsLoading[collaborationId] ?? false,
  )
  const uploading = useSelector(
    (state: RootState) => state.documents.collaborationContractUploading[collaborationId] ?? false,
  )
  const deleting = useSelector(
    (state: RootState) => state.documents.collaborationDocumentDeleting ?? EMPTY_DELETING,
  )

  useEffect(() => {
    if (collaborationId) {
      dispatch(fetchCollaborationDocuments(collaborationId))
    }
  }, [collaborationId, dispatch])

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const handleOpenDocument = async (docId: string) => {
    setOpeningDocId(docId)
    try {
      const response = await athleteApi.get(
        `/athlete/campaigns/${collaborationId}/documents/${docId}/download-url`,
      )
      window.open(response.data.downloadUrl, '_blank', 'noopener,noreferrer')
    } catch {
      // fall through silently
    } finally {
      setOpeningDocId(null)
    }
  }

  const handleDeleteDocument = async (docId: string) => {
    const result = await dispatch(
      deleteCollaborationDocument({ collaborationId, documentId: docId }),
    )
    if (deleteCollaborationDocument.fulfilled.match(result)) {
      dispatch(
        collaborationsApi.util.invalidateTags([
          { type: 'CollaborationDetail', id: collaborationId },
        ]),
      )
    }
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setUploadError(null)

    if (file.size > 20 * 1024 * 1024) {
      setUploadError('File must be under 20 MB.')
      return
    }

    const result = await dispatch(uploadCollaborationContract({ collaborationId, file }))

    if (uploadCollaborationContract.fulfilled.match(result)) {
      // Invalidate RTK Query cache so ComplianceTab refreshes campaign.contractUrl and triggers scan
      dispatch(
        collaborationsApi.util.invalidateTags([
          { type: 'CollaborationDetail', id: collaborationId },
        ]),
      )
    }

    // Reset input so the same file can be re-selected if needed
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const hasContract = documents.some((d) => d.documentType === 'contract')

  return (
    <div className="bg-gray-900/90 backdrop-blur-xs border border-white/5 rounded-3xl rounded-bl-none p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <FileText className="h-5 w-5 text-[#C0FD72]" />
          <h3 className="text-white font-semibold text-lg">Agreements & Documents</h3>
        </div>

        {/* Upload contract button */}
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx"
            className="hidden"
            onChange={handleFileChange}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-white/8 border border-white/15 hover:border-white/30 text-white/70 hover:text-white transition-all disabled:opacity-50"
          >
            {uploading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Upload className="h-3.5 w-3.5" />
            )}
            {uploading ? 'Uploading…' : 'Upload Contract'}
          </button>
        </div>
      </div>

      {uploadError && <p className="text-red-400 text-xs mb-3">{uploadError}</p>}

      {/* Contract upload hint */}
      {!hasContract && !loading && (
        <div className="flex items-start gap-2.5 bg-[#C0FD72]/5 border border-[#C0FD72]/15 rounded-xl px-4 py-3 mb-4">
          <ShieldCheck className="w-4 h-4 text-[#C0FD72] shrink-0 mt-0.5" />
          <p className="text-white/50 text-xs leading-relaxed">
            Upload your signed contract to enable the AI compliance scan and pre-fill your NIL Go
            submission form.
          </p>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-8 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          <span className="text-sm">Loading documents...</span>
        </div>
      )}

      {!loading && documents.length === 0 && (
        <p className="text-gray-400 text-sm text-center py-4">No documents yet.</p>
      )}

      {!loading && documents.length > 0 && (
        <div className="space-y-3">
          {documents.map((doc) => (
            <div
              key={doc.id}
              className="flex items-center justify-between p-3 bg-white/5 rounded-xl border border-white/10"
            >
              <div className="flex items-center gap-3 min-w-0">
                <FileText className="h-5 w-5 text-red-400 shrink-0" />
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-white text-sm font-medium truncate">
                      {doc.title || doc.name}
                    </p>
                    {doc.documentType === 'contract' && (
                      <span className="shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-[#C0FD72]/15 text-[#C0FD72]">
                        Contract
                      </span>
                    )}
                  </div>
                  <p className="text-gray-400 text-xs">{formatDate(doc.uploadedAt)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0 ml-3">
                <button
                  onClick={() => handleOpenDocument(doc.id)}
                  disabled={openingDocId === doc.id || !!deleting[doc.id]}
                  className="flex items-center gap-1.5 text-[#C0FD72] hover:text-white text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {openingDocId === doc.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <ExternalLink className="h-4 w-4" />
                      View
                    </>
                  )}
                </button>
                <button
                  onClick={() => handleDeleteDocument(doc.id)}
                  disabled={!!deleting[doc.id] || openingDocId === doc.id}
                  className="flex items-center text-white/30 hover:text-red-400 transition-colors disabled:opacity-50"
                  title="Delete document"
                >
                  {deleting[doc.id] ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default CollaborationDocumentsCard
